// ── Enums / string unions ──────────────────────────────────────────────────────

export type CardType =
  | 'Visa'
  | 'Mastercard'
  | 'Amex'
  | 'UnionPay'
  | 'JCB'
  | 'Discover'
  | 'Other';

export type CardClearingStatus =
  | 'Pending'
  | 'PartiallyMatched'
  | 'Matched'
  | 'Posted'
  | 'Cancelled';

export type CardMatchStatus =
  | 'Unmatched'
  | 'Matched'
  | 'PartiallyMatched'
  | 'Variance'
  | 'Posted'
  | 'Reversed';

export type CardReconciliationStatus =
  | 'Draft'
  | 'InProgress'
  | 'Submitted'
  | 'Approved'
  | 'Rejected'
  | 'Posted'
  | 'Reversed';

export type ChargebackStatus =
  | 'Received'
  | 'Accepted'
  | 'Disputed'
  | 'Recovered'
  | 'WrittenOff';

// ── Status display helpers ────────────────────────────────────────────────────

export const CARD_RECON_STATUS_LABELS: Record<CardReconciliationStatus, string> = {
  Draft:       'Draft',
  InProgress:  'In Progress',
  Submitted:   'Submitted',
  Approved:    'Approved',
  Rejected:    'Rejected',
  Posted:      'Posted',
  Reversed:    'Reversed',
};

export const CARD_RECON_STATUS_COLORS: Record<CardReconciliationStatus, string> = {
  Draft:      'bg-gray-100 text-gray-700',
  InProgress: 'bg-blue-100 text-blue-700',
  Submitted:  'bg-indigo-100 text-indigo-700',
  Approved:   'bg-green-100 text-green-700',
  Rejected:   'bg-red-100 text-red-700',
  Posted:     'bg-purple-100 text-purple-700',
  Reversed:   'bg-amber-100 text-amber-700',
};

// ── Card Acquirer ─────────────────────────────────────────────────────────────

export interface CardAcquirerListDto {
  id:                 string;
  acquirerCode:       string;
  acquirerName:       string;
  mdrRate:            number;
  settlementDelayDays: number;
  isActive:           boolean;
}

export interface CardAcquirerDto {
  id:                             string;
  acquirerCode:                   string;
  acquirerName:                   string;
  bankId?:                        string | null;
  bankName?:                      string | null;
  settlementBankAccountId?:       string | null;
  settlementBankAccountName?:     string | null;
  currencyId?:                    string | null;
  currencyCode?:                  string | null;
  mdrRate:                        number;
  settlementDelayDays:            number;
  cardClearingGLAccountId?:       string | null;
  cardClearingGLAccountName?:     string | null;
  cardChargesExpenseAccountId?:   string | null;
  cardChargesExpenseAccountName?: string | null;
  chargebackAccountId?:           string | null;
  chargebackAccountName?:         string | null;
  varianceAccountId?:             string | null;
  varianceAccountName?:           string | null;
  isActive:                       boolean;
  remarks?:                       string | null;
}

// ── Card Clearing Items ───────────────────────────────────────────────────────

export interface CardClearingItemDto {
  id:                     string;
  companyId:              string;
  financialYearCode:      string;
  financialPeriodCode:    string;
  locationId?:            string | null;
  locationName?:          string | null;
  cardAcquirerId:         string;
  acquirerName:           string;
  cardType:               string;
  cardTypeName:           string;
  terminalId?:            string | null;
  batchNumber?:           string | null;
  saleDate:               string;
  expectedSettlementDate: string;
  grossAmount:            number;
  expectedMdrAmount:      number;
  expectedNetAmount:      number;
  sourceDocument?:        string | null;
  sourceDocumentNo?:      string | null;
  status:                 number;
  statusName:             string;
  /** The terminal's approval code, when the POS sends it. */
  approvalCode?:          string | null;
  /** Last four digits of the card, never more. */
  cardLast4?:             string | null;
}

/** Card money each acquirer still owes, by days past its expected settlement date. */
export interface CardReceivablesAgeingRowDto {
  cardAcquirerId: string;
  acquirerCode:   string;
  acquirerName:   string;
  itemCount:      number;
  notYetDue:      number;
  days0To3:       number;
  days4To7:       number;
  days8To15:      number;
  days16To30:     number;
  over30Days:     number;
  total:          number;
}

export interface CardClearingItemFilterDto {
  companyId?:       string;
  cardAcquirerId?:  string;
  dateFrom?:        string;
  dateTo?:          string;
  status?:          number;
  search?:          string;
  page?:            number;
  pageSize?:        number;
}

// ── Card Settlement ───────────────────────────────────────────────────────────

export interface CardSettlementLineDto {
  id:                  string;
  lineNo:              number;
  acquirerName:        string;
  cardTypeName:        string;
  terminalId?:         string | null;
  batchNumber?:        string | null;
  settlementDate:      string;
  grossAmount:         number;
  mdrAmount:           number;
  netSettlementAmount: number;
  chargebackAmount:    number;
  adjustmentAmount:    number;
  bankReference?:      string | null;
  settlementReference?: string | null;
  matchStatusName:     string;
}

export interface CardSettlementBatchDto {
  id:                string;
  companyId:         string;
  settlementBatchNo: string;
  importDate:        string;
  cardAcquirerId:    string;
  acquirerName:      string;
  fileReference?:    string | null;
  importSource?:     string | null;
  totalLines:        number;
  totalGrossAmount:  number;
  totalMdrAmount:    number;
  totalNetAmount:    number;
  importStatus:      string;
  importErrors?:     string | null;
  remarks?:          string | null;
  lines:             CardSettlementLineDto[];
}

export interface CardSettlementBatchListDto {
  id:                string;
  settlementBatchNo: string;
  importDate:        string;
  acquirerName:      string;
  fileReference?:    string | null;
  totalLines:        number;
  totalGrossAmount:  number;
  totalNetAmount:    number;
  importStatus:      string;
}

export interface CardSettlementBatchFilterDto {
  companyId?:      string;
  cardAcquirerId?: string;
  dateFrom?:       string;
  dateTo?:         string;
  importStatus?:   string;
  search?:         string;
  page?:           number;
  pageSize?:       number;
}

export interface CardSettlementImportRowDto {
  lineNo:               number;
  terminalId?:          string | null;
  batchNumber?:         string | null;
  cardTypeName?:        string | null;
  settlementDate:       string;
  grossAmount:          number;
  mdrAmount:            number;
  chargebackAmount:     number;
  adjustmentAmount:     number;
  bankReference?:       string | null;
  settlementReference?: string | null;
  merchantId?:          string | null;
}

// ── Card Reconciliation ───────────────────────────────────────────────────────

export interface CardReconciliationLineDto {
  id:                    string;
  lineNo:                number;
  cardClearingItemId?:   string | null;
  clearingBatchNumber?:  string | null;
  clearingSaleDate?:     string | null;
  cardSettlementLineId?: string | null;
  settlementBatchNumber?: string | null;
  settlementDate?:       string | null;
  matchType:             string;
  matchConfidence:       number;
  clearingGrossAmount:   number;
  settlementGrossAmount: number;
  mdrExpected:           number;
  mdrActual:             number;
  mdrVariance:           number;
  netExpected:           number;
  netSettled:            number;
  variance:              number;
  varianceReason?:       string | null;
  matchStatusName:       string;
  isPosted:              boolean;
}

export interface CardReconciliationDto {
  id:                     string;
  companyId:              string;
  reconciliationNo:       string;
  financialYearCode:      string;
  financialPeriodCode:    string;
  locationId?:            string | null;
  locationName?:          string | null;
  cardAcquirerId:         string;
  acquirerName:           string;
  cardType?:              string | null;
  cardTypeName?:          string | null;
  settlementBankAccountId?: string | null;
  bankAccountName?:       string | null;
  dateFrom:               string;
  dateTo:                 string;
  expectedGrossAmount:    number;
  settledGrossAmount:     number;
  expectedMdrAmount:      number;
  actualMdrAmount:        number;
  expectedNetAmount:      number;
  bankSettledAmount:      number;
  differenceAmount:       number;
  varianceStatus?:        string | null;
  reconciliationStatus:   number;
  statusName:             string;
  submittedBy?:           string | null;
  submittedAt?:           string | null;
  approvedBy?:            string | null;
  approvedAt?:            string | null;
  postedBy?:              string | null;
  postedAt?:              string | null;
  rejectionReason?:       string | null;
  journalId?:             string | null;
  remarks?:               string | null;
  lines:                  CardReconciliationLineDto[];
}

export interface CardReconciliationListDto {
  id:                   string;
  reconciliationNo:     string;
  financialPeriodCode:  string;
  acquirerName:         string;
  cardTypeName?:        string | null;
  dateFrom:             string;
  dateTo:               string;
  expectedGrossAmount:  number;
  differenceAmount:     number;
  reconciliationStatus: number;
  statusName:           string;
  createdDate:          string;
}

export interface CardReconciliationFilterDto {
  companyId?:          string;
  cardAcquirerId?:     string;
  financialPeriodCode?: string;
  dateFrom?:           string;
  dateTo?:             string;
  status?:             number;
  search?:             string;
  page?:               number;
  pageSize?:           number;
}

export interface AutoMatchRequest {
  amountTolerance:    number;
  dateToleranceDays:  number;
}

/** What POST /card-reconciliations/{id}/auto-match returns. */
export interface AutoMatchResult {
  exactMatches:       number;
  fuzzyMatches:       number;
  unmatched:          number;
  totalLines:         number;
}

// ── Card Chargebacks ──────────────────────────────────────────────────────────

export interface CardChargebackDto {
  id:                    string;
  companyId:             string;
  chargebackNo:          string;
  cardAcquirerId:        string;
  acquirerName:          string;
  originalSaleReference?: string | null;
  chargebackDate:        string;
  chargebackReason?:     string | null;
  amount:                number;
  status:                number;
  statusName:            string;
  recoveryStatus?:       string | null;
  notes?:                string | null;
  journalId?:            string | null;
  createdDate:           string;
}

export interface CardChargebackFilterDto {
  companyId?:      string;
  cardAcquirerId?: string;
  status?:         number;
  dateFrom?:       string;
  dateTo?:         string;
  search?:         string;
  page?:           number;
  pageSize?:       number;
}

// ── Create request types ──────────────────────────────────────────────────────

export interface CreateCardAcquirerRequest {
  acquirerCode:               string;
  acquirerName:               string;
  bankId?:                    string | null;
  settlementBankAccountId?:   string | null;
  currencyId?:                string | null;
  mdrRate:                    number;
  settlementDelayDays:        number;
  cardClearingGLAccountId?:   string | null;
  cardChargesExpenseAccountId?: string | null;
  chargebackAccountId?:       string | null;
  varianceAccountId?:         string | null;
  remarks?:                   string | null;
}

export interface CreateCardReconciliationRequest {
  reconciliationNo:        string;
  financialYearCode:       string;
  financialPeriodCode:     string;
  locationId?:             string | null;
  cardAcquirerId:          string;
  cardType?:               string | null;
  settlementBankAccountId?: string | null;
  dateFrom:                string;
  dateTo:                  string;
  currencyId?:             string | null;
  remarks?:                string | null;
}

export interface ImportSettlementBatchRequest {
  settlementBatchNo: string;
  cardAcquirerId:    string;
  bankAccountId?:    string | null;
  currencyId?:       string | null;
  fileReference?:    string | null;
  importSource?:     string | null;
  remarks?:          string | null;
  rows:              CardSettlementImportRowDto[];
}

export interface ManualMatchRequest {
  cardClearingItemId:    string;
  cardSettlementLineId:  string;
  varianceReason?:       string | null;
}
